import React, { FormEvent, useCallback, useEffect, useRef, useState } from 'react';
import api from '../../api/api';
import RestaurantHallsPayrollPanel, { RestaurantHallsReview } from '../../components/RestaurantHallsPayrollPanel';
import PersonalReportDeliveryPanel from '../../components/PersonalReportDeliveryPanel';
import PersonalEmployeeStatementPrint from '../../components/PersonalEmployeeStatementPrint';
import { OPERATING_GROUP_CHANGED_IN_ANOTHER_TAB_MESSAGE, useSelectedOperatingGroup } from '../../hooks/useSelectedOperatingGroup';
import { isRestaurantOperatingContext } from '../../utils/operatingContext';
import { formatRestaurantPayrollDecimal } from '../../utils/restaurantPayrollFormat';
import { currentRestaurantMonth, isRestaurantMonth } from '../../utils/restaurantMonth';
import './SalaryPayrollPage.css';

type PayrollEmployee = {
  employmentId: number;
  employeeName: string;
  departmentName: string;
  position: string;
  method: 'fixed-plus-revenue' | 'hourly-coefficient' | 'revenue-weighted-hours' | 'halls-fund';
  workedHours: number;
  hourlyRate: number;
  evaluationCoefficient: number;
  evaluationCoefficientSource: string;
  salaryHours: number;
  baseSalary: number;
  fixedBonus: number;
  bonusPercent: number;
  bonus: number;
  fixedPenalty: number;
  penaltyPercent: number;
  penalty: number;
  fixedMonthlyRate: number;
  revenuePercent: number;
  monthlyRevenue: number;
  amount: number;
  policySource: string;
  policyEffectiveYear: number;
  policyEffectiveMonth: number;
  adjustments: Array<{ amount: number; reason: string; createdAtUtc: string; createdByUsername: string }>;
};

type PayrollPeriod = {
  exists: boolean;
  year: number;
  month: number;
  status: 'draft' | 'approved' | 'closed' | 'cancelled';
  revision: number;
  version: number;
  monthlyRevenue: number;
  salaryFundPercent: number;
  salaryFund: number;
  totalSalaryHours: number;
  hourRate: number;
  total: number;
  issues: string[];
  readinessTargets?: { kind: 'employment' | 'rules' | 'revenue' | 'halls'; employmentId: number | null; label: string }[];
  openAttendanceWarnings?: Array<{ employmentId: number; employeeName: string; openIntervals: number }>;
  openAttendanceVersion?: string | null;
  previewVersion?: string | null;
  employees: PayrollEmployee[];
  halls?: RestaurantHallsReview | null;
  legacyHallsHistory?: {
    status: string;
    departmentNameSnapshot: string;
    orderRevenueSnapshot: number;
    totalPoolSnapshot: number;
    employees: Array<{ employeeName: string; amount: number }>;
  } | null;
  approvedAtUtc?: string;
  approvedByUsername?: string;
  closedAtUtc?: string;
  closedByUsername?: string;
  cancelledAtUtc?: string;
  cancelledByUsername?: string;
  cancellationReason?: string;
  cancellationHistory?: Array<{
    revision: number;
    cancellationReason: string;
    cancelledAtUtc: string;
    cancelledByUsername: string;
    totalSnapshot: number;
    employees: Array<{
      employeeName: string;
      positionSnapshot: string;
      calculationMethod: string;
      workedHours: number;
      baseSalary: number;
      bonus: number;
      penalty: number;
      amount: number;
    }>;
  }>;
};

type SalaryRuleState = 'active' | 'unmapped' | 'superseded' | 'retired' | 'future' | 'inactive' | 'conflict';
const ruleStateLabels: Record<SalaryRuleState, string> = {
  active: 'Чинне на своєму рівні', unmapped: 'Потребує зіставлення або припинення',
  superseded: 'Замінена ревізія — не застосовується', retired: 'Припинене на вибраний місяць',
  future: 'Ще не набуло чинності', inactive: 'Неактивне', conflict: 'Конфлікт посадових правил',
};
const canRetireRule = (state?: SalaryRuleState) => state === 'active' || state === 'unmapped' || state === 'conflict';

type RuleCoverage = {
  applied: Array<{ employmentId: number; fullName: string }>;
  blocked: Array<{ employmentId: number; fullName: string }>;
};
type RuleRetirementImpact = {
  kind: 'policy' | 'rate';
  id: number;
  ruleVersion: number;
  version: string;
  employees: Array<{ employmentId: number; fullName: string; beforeSource: string; afterSource: string }>;
};
type RuleChangeImpact = {
  version: string;
  employees: RuleRetirementImpact['employees'];
  unchangedEmployments: number;
  hallsEmployments: number;
};
type RuleChangePayload = {
  effectiveYear: number; effectiveMonth: number; positionId: number | null;
  scope?: string; employmentId?: number | null; departmentId?: number | null;
  calculationMethod?: PayrollEmployee['method']; fixedMonthlyRate?: number; revenuePercent?: number;
  hourlyRateOverride?: number | null; evaluationCoefficientOverride?: number | null; baseRate?: number;
};
const RuleCoverageDetails: React.FC<{ coverage?: RuleCoverage }> = ({ coverage }) => coverage
  ? <details className="salary-payroll__disclosure"><summary>Джерело правила для {coverage.applied.length} працевлаштувань; блокує {coverage.blocked.length}</summary>
    {coverage.applied.length > 0 && <ul aria-label="Працевлаштування за правилом">
      {coverage.applied.map(item => <li key={item.employmentId}>{item.fullName}</li>)}</ul>}
    {coverage.blocked.length > 0 && <><p>Потребують усунення конфлікту або непов’язаної ставки:</p>
      <ul>{coverage.blocked.map(item => <li key={item.employmentId}>{item.fullName}</li>)}</ul></>}
  </details>
  : <p>Охоплення не підтверджено. Оновіть дані перед прийняттям рішення.</p>;

type PayrollOptions = {
  employees: Array<{
    employmentId: number;
    fullName: string;
    position: string;
    positionId: number | null;
    departmentId: number | null;
    departmentName: string;
    baseRate: number;
  }>;
  positions: Array<{ id: number; name: string }>;
  departments: Array<{ departmentId: number; name: string }>;
  policies: Array<{
    coverage?: RuleCoverage;
    state?: SalaryRuleState;
    id: number;
    scopeKey: string;
    employmentId?: number | null;
    positionId?: number | null;
    departmentId?: number | null;
    effectiveYear: number;
    effectiveMonth: number;
    retiredEffectiveYear?: number | null;
    retiredEffectiveMonth?: number | null;
    revision: number;
    version: number;
    calculationMethod: PayrollEmployee['method'];
    fixedMonthlyRate: number;
    revenuePercent: number;
    hourlyRateOverride?: number | null;
    evaluationCoefficientOverride?: number | null;
  }>;
  salaryRules: Array<{
    coverage?: RuleCoverage;
    state?: SalaryRuleState;
    id: number;
    position: string;
    positionId: number | null;
    effectiveYear: number;
    effectiveMonth: number;
    retiredEffectiveYear?: number | null;
    retiredEffectiveMonth?: number | null;
    revision: number;
    version: number;
    baseRate: number;
    isActive: boolean;
  }>;
  revenue?: { amount: number; notes: string; version: number } | null;
  retirementImpact?: RuleRetirementImpact | null;
};

type DraftInputs = { fixedBonus: string; bonusPercent: string; fixedPenalty: string; penaltyPercent: string };

const currency = new Intl.NumberFormat('uk-UA', { style: 'currency', currency: 'UAH' });
const DISCLOSURE_STORAGE_KEY = 'crm4chef:restaurant-payroll:sections:v1';
type DisclosureKey = 'halls' | 'revenue' | 'policies' | 'bonuses' | 'adjustments';
type DisclosureState = Record<DisclosureKey, boolean>;
const defaultDisclosureState: DisclosureState = {
  halls: false,
  revenue: false,
  policies: false,
  bonuses: false,
  adjustments: false,
};
const policyScopeLabels: Record<string, string> = {
  global: 'Усього ресторану', 'employment-default': 'Базова ставка працевлаштування',
  'unmapped-position': 'Блокер: непов’язана історична ставка', 'position-conflict': 'Блокер: конфлікт посадових правил',
};
const calculationMethodLabels: Record<string, string> = {
  'fixed-plus-revenue': 'Фіксована сума + частка виторгу',
  'hourly-coefficient': 'Години × ставка × коефіцієнт',
  'revenue-weighted-hours': 'Фонд за зваженими годинами',
};
const readDisclosureState = (): DisclosureState => {
  const stored = localStorage.getItem(DISCLOSURE_STORAGE_KEY);
  if (!stored) return defaultDisclosureState;
  try {
    const parsed: unknown = JSON.parse(stored);
    if (typeof parsed !== 'object' || parsed === null) return defaultDisclosureState;
    const saved = parsed as Record<string, unknown>;
    return {
      halls: typeof saved.halls === 'boolean' ? saved.halls : false,
      revenue: typeof saved.revenue === 'boolean' ? saved.revenue : false,
      policies: typeof saved.policies === 'boolean' ? saved.policies : false,
      bonuses: typeof saved.bonuses === 'boolean' ? saved.bonuses : false,
      adjustments: typeof saved.adjustments === 'boolean' ? saved.adjustments : false,
    };
  } catch {
    return defaultDisclosureState;
  }
};
const sumDisplayedMoney = (values: number[]) => values.reduce((sum, value) => sum + Math.round(value * 100), 0) / 100;
const LAST_MONTH_KEY = 'crm4chef.restaurant-payroll.last-month.v1';
const readPayrollMonth = () => {
  try {
    const saved = localStorage.getItem(LAST_MONTH_KEY);
    return isRestaurantMonth(saved) ? saved : currentRestaurantMonth();
  } catch (failure) {
    console.warn('Не вдалося прочитати збережений місяць зарплати.', failure);
    return currentRestaurantMonth();
  }
};
const policyScopeLabel = (scopeKey: string, options?: PayrollOptions) => {
  if (policyScopeLabels[scopeKey]) return policyScopeLabels[scopeKey];
  const employment = /^employment:(\d+)$/.exec(scopeKey);
  if (employment) {
    const employee = options?.employees.find(item => item.employmentId === Number(employment[1]));
    return employee ? `Особисте правило · ${employee.fullName}` : `Особисте правило · №${employment[1]}`;
  }
  const position = /^position:(\d+)$/.exec(scopeKey);
  if (position) {
    const item = options?.positions.find(candidate => candidate.id === Number(position[1]));
    return item ? `Посадове правило · ${item.name}` : `Посадове правило · №${position[1]}`;
  }
  const department = /^department:(\d+)$/.exec(scopeKey);
  if (department) {
    const item = options?.departments.find(candidate => candidate.departmentId === Number(department[1]));
    return item ? `Правило підрозділу · ${item.name}` : `Правило підрозділу · №${department[1]}`;
  }
  return scopeKey;
};
const requestKey = () => (
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`
);
const messageFrom = (error: unknown, fallback: string) => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string } } }).response;
    if (response?.data?.message) return response.data.message;
  }
  return fallback;
};

const RestaurantPayrollPage: React.FC = () => {
  const { groupId, changedInAnotherTab } = useSelectedOperatingGroup();
  if (changedInAnotherTab)
    return <main className="salary-payroll"><p role="alert">{OPERATING_GROUP_CHANGED_IN_ANOTHER_TAB_MESSAGE}</p></main>;
  if (!groupId || !isRestaurantOperatingContext(groupId))
    return <main className="salary-payroll"><p role="alert">Оберіть одну операційну групу для роботи із зарплатою ресторану.</p></main>;
  return <RestaurantPayrollWorkspace key={groupId} groupId={groupId} />;
};

const RestaurantPayrollWorkspace: React.FC<{ groupId: string }> = ({ groupId }) => {
  const [retirementReason, setRetirementReason] = useState('');
  const [retirementReview, setRetirementReview] = useState<(RuleRetirementImpact & { reason: string }) | null>(null);
  const [ruleChangeReview, setRuleChangeReview] = useState<{
    kind: 'policy' | 'rate'; payload: RuleChangePayload; impact: RuleChangeImpact;
  } | null>(null);
  const ruleChangeGeneration = useRef(0);
  const ruleChangePanel = useRef<HTMLElement>(null);
  useEffect(() => {
    if (!ruleChangeReview) return;
    const frame = window.requestAnimationFrame(() => {
      ruleChangePanel.current?.focus({ preventScroll: true });
      ruleChangePanel.current?.scrollIntoView({ block: 'start' });
    });
    return () => window.cancelAnimationFrame(frame);
  }, [ruleChangeReview]);
  const [employeeSort, setEmployeeSort] = useState('name');
  const [month, setMonth] = useState(readPayrollMonth);
  const [previousPeriod, setPreviousPeriod] = useState<PayrollPeriod | null>(null);
  const [comparisonNotice, setComparisonNotice] = useState('');
  const [comparing, setComparing] = useState(false);
  const comparisonGeneration = useRef(0);
  const [deliveryEmploymentId, setDeliveryEmploymentId] = useState<number | null>(null);
  const [printEmploymentId, setPrintEmploymentId] = useState<number | null>(null);
  const pendingReportFocus = useRef<'delivery' | 'print' | null>(null);
  const deliveryHeading = useRef<HTMLHeadingElement>(null);
  const printHeading = useRef<HTMLHeadingElement>(null);
  useEffect(() => {
    const requested = pendingReportFocus.current;
    pendingReportFocus.current = null;
    if (!requested) return;
    const heading = requested === 'delivery' ? deliveryHeading.current : printHeading.current;
    heading?.focus({ preventScroll: true });
    heading?.scrollIntoView({ block: 'center' });
  }, [deliveryEmploymentId, printEmploymentId]);
  const [period, setPeriod] = useState<PayrollPeriod | null>(null);
  const [options, setOptions] = useState<PayrollOptions | null>(null);
  const [canEdit, setCanEdit] = useState(false);
  const [canViewPersonnel, setCanViewPersonnel] = useState(false);
  const [personnelAccessNotice, setPersonnelAccessNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [revenueAmount, setRevenueAmount] = useState('0');
  const [revenueNotes, setRevenueNotes] = useState('');
  const [selectedEmploymentId, setSelectedEmploymentId] = useState('');
  const [draftInputs, setDraftInputs] = useState<Record<number, DraftInputs>>({});
  const [policyScope, setPolicyScope] = useState<'global' | 'employment' | 'position' | 'department'>('global');
  const [policyEmploymentId, setPolicyEmploymentId] = useState('');
  const [policyPositionId, setPolicyPositionId] = useState('');
  const [policyDepartmentId, setPolicyDepartmentId] = useState('');
  const [policyMethod, setPolicyMethod] = useState<PayrollEmployee['method']>('hourly-coefficient');
  const [policyFixedRate, setPolicyFixedRate] = useState('0');
  const [policyRevenuePercent, setPolicyRevenuePercent] = useState('0');
  const [policyHourlyRate, setPolicyHourlyRate] = useState('');
  const [policyCoefficient, setPolicyCoefficient] = useState('');
  const [salaryRulePositionId, setSalaryRulePositionId] = useState('');
  const [salaryRuleRate, setSalaryRuleRate] = useState('');
  const [adjustmentEmploymentId, setAdjustmentEmploymentId] = useState('');
  const [adjustmentAmount, setAdjustmentAmount] = useState('');
  const [adjustmentReason, setAdjustmentReason] = useState('');
  const [adjustmentKey, setAdjustmentKey] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const [disclosures, setDisclosures] = useState(readDisclosureState);
  const hallsDisclosure = useRef<HTMLDetailsElement>(null);
  const mounted = useRef(true);
  const currentMonthRef = useRef(month);
  const requestVersion = useRef(0);
  currentMonthRef.current = month;
  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const [yearText, monthText] = month.split('-');
  const year = Number(yearText);
  const monthNumber = Number(monthText);

  const load = useCallback(async (active: { current: boolean }) => {
    if (!mounted.current || currentMonthRef.current !== month || !year || monthNumber < 1 || monthNumber > 12) return;
    const version = ++requestVersion.current;
    const isCurrent = () => active.current && mounted.current &&
      currentMonthRef.current === month && requestVersion.current === version;
    setBusy(true);
    setRetirementReview(null);
    setRuleChangeReview(null);
    ruleChangeGeneration.current++;
    setPeriod(null);
    setOptions(null);
    setCanEdit(false);
    setCanViewPersonnel(false);
    setPersonnelAccessNotice('');
    setError('');
    comparisonGeneration.current += 1;
    setPreviousPeriod(null);
    setComparisonNotice('');
    setComparing(false);
    try {
      const [periodResponse, optionsResponse, accessResponse] = await Promise.all([
        api.get<PayrollPeriod>(`/restaurant-payroll/${year}/${monthNumber}`),
        api.get<PayrollOptions>('/restaurant-payroll/options', { params: { year, month: monthNumber } }),
        api.get<{ permissions?: string[]; editPermissions: string[] }>('/users/me/access'),
      ]);
      if (!isCurrent()) return;
      if (accessResponse.data.permissions?.includes('page.admin')) {
        setPersonnelAccessNotice('Перевірка кадрового доступу цього ресторану… Переходи до табеля, карток і звітів поки недоступні.');
        void api.get<{ operatingGroupId: number; managePersonnel: boolean }>('/restaurant-personnel/context').then(context => {
          if (!isCurrent()) return;
          const admitted = context.data.operatingGroupId === Number(groupId) && context.data.managePersonnel === true;
          setCanViewPersonnel(admitted);
          setPersonnelAccessNotice(admitted ? '' : 'Перехід до табеля, карток і звітів персоналу недоступний: у цьому ресторані не надано кадровий доступ ManagePersonnel. Попросіть адміністратора перевірити локальні права.');
        }).catch(failure => {
          if (!isCurrent()) return;
          setPersonnelAccessNotice(`Переходи до табеля, карток і звітів приховано: ${messageFrom(failure,
            'Не вдалося перевірити кадровий доступ цього ресторану.')} Оновіть сторінку для повторної перевірки.`);
        });
      } else {
        setPersonnelAccessNotice('Перехід до табеля, карток і звітів персоналу недоступний: потрібні дозвіл перегляду персоналу (page.admin) та кадровий доступ цього ресторану. Дозвіл на зарплату їх не надає.');
      }
      const loadedPeriod = periodResponse.data;
      setPeriod(loadedPeriod);
      setOptions(optionsResponse.data);
      setCanEdit(accessResponse.data.editPermissions.includes('page.management.salary'));
      setRevenueAmount(String(optionsResponse.data.revenue?.amount ?? 0));
      setRevenueNotes(optionsResponse.data.revenue?.notes ?? '');
      const employee = loadedPeriod.employees[0]?.employmentId ?? optionsResponse.data.employees[0]?.employmentId;
      setSelectedEmploymentId(employee == null ? '' : String(employee));
      setAdjustmentEmploymentId(loadedPeriod.employees[0]?.employmentId == null ? '' : String(loadedPeriod.employees[0].employmentId));
      setDraftInputs(Object.fromEntries(loadedPeriod.employees.map(item => [
        item.employmentId,
        {
          fixedBonus: String(item.fixedBonus),
          bonusPercent: String(item.bonusPercent),
          fixedPenalty: String(item.fixedPenalty),
          penaltyPercent: String(item.penaltyPercent),
        },
      ])));
    } catch (loadError) {
      if (isCurrent()) setError(messageFrom(loadError, 'Не вдалося завантажити зарплатний період ресторану.'));
    } finally {
      if (isCurrent()) setBusy(false);
    }
  }, [year, monthNumber, month, groupId]);

  useEffect(() => {
    const active = { current: true };
    void load(active);
    return () => { active.current = false; };
  }, [load]);

  const reload = async () => {
    await load({ current: true });
  };

  const selectMonth = (value: string) => {
    if (!isRestaurantMonth(value)) return;
    setMonth(value);
    try { localStorage.setItem(LAST_MONTH_KEY, value); }
    catch (failure) {
      console.warn('Не вдалося зберегти місяць зарплати.', failure);
      setNotice('Місяць змінено, але браузер не дозволяє запам’ятати його для наступного відкриття.');
    }
  };
  const comparePreviousPeriod = async () => {
    const generation = ++comparisonGeneration.current;
    const previousYear = monthNumber === 1 ? year - 1 : year;
    const previousMonth = monthNumber === 1 ? 12 : monthNumber - 1;
    const isCurrent = () => mounted.current && currentMonthRef.current === month &&
      generation === comparisonGeneration.current;
    setComparing(true);
    setPreviousPeriod(null);
    setComparisonNotice('');
    try {
      const response = await api.get<PayrollPeriod>(`/restaurant-payroll/${previousYear}/${previousMonth}`);
      if (!isCurrent()) return;
      if (response.data.exists && ['approved', 'closed'].includes(response.data.status) &&
          response.data.year === previousYear && response.data.month === previousMonth) {
        setPreviousPeriod(response.data);
      } else setComparisonNotice('Немає фіналізованого попереднього місяця для порівняння. Чернетки та скасовані редакції не порівнюються.');
    } catch (failure) {
      if (isCurrent()) setComparisonNotice(messageFrom(failure, 'Не вдалося прочитати попередній місяць. Порівняння недоступне.'));
    } finally { if (isCurrent()) setComparing(false); }
  };

  const runAction = async (action: () => Promise<void>, successMessage: string) => {
    const isCurrent = () => mounted.current && currentMonthRef.current === month;
    if (!isCurrent() || busy || !canEdit) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await action();
      if (!isCurrent()) return;
      await reload();
      if (isCurrent()) setNotice(successMessage);
    } catch (actionError) {
      if (isCurrent()) setError(messageFrom(actionError, 'Не вдалося виконати дію.'));
    } finally {
      if (isCurrent()) setBusy(false);
    }
  };

  const saveRevenue = (event: FormEvent) => {
    event.preventDefault();
    void runAction(async () => {
      await api.put(`/restaurant-payroll/revenue/${year}/${monthNumber}`, {
        version: options?.revenue?.version ?? 0,
        amount: Number(revenueAmount),
        notes: revenueNotes,
      });
    }, 'Виторг ресторану збережено окремо від інших груп.');
  };

  const reviewRuleChange = async (kind: 'policy' | 'rate', payload: RuleChangePayload) => {
    if (busy || !canEdit) return;
    const generation = ++ruleChangeGeneration.current;
    const request = requestVersion.current;
    const isCurrent = () => mounted.current && currentMonthRef.current === month &&
      ruleChangeGeneration.current === generation;
    setBusy(true);
    setError('');
    setNotice('');
    setRuleChangeReview(null);
    try {
      const { data } = await api.put<RuleChangeImpact>(
        `/restaurant-payroll/${kind === 'policy' ? 'policies' : 'salary-rules'}`, payload,
        { params: { previewOnly: true } });
      if (!isCurrent()) return;
      if (!data.version || !Array.isArray(data.employees) ||
          !Number.isInteger(data.unchangedEmployments) || !Number.isInteger(data.hallsEmployments))
        throw new Error('Сервер не підтвердив вплив правила. Повторіть перегляд.');
      setRuleChangeReview({ kind, payload, impact: data });
    } catch (failure) {
      if (isCurrent()) setError(messageFrom(failure, 'Не вдалося перевірити вплив нового правила.'));
    } finally {
      if (mounted.current && currentMonthRef.current === month && requestVersion.current === request) setBusy(false);
    }
  };
  const invalidateRuleChange = () => {
    ruleChangeGeneration.current++;
    setRuleChangeReview(null);
  };
  const savePolicy = (event: FormEvent) => {
    event.preventDefault();
    void reviewRuleChange('policy', {
        scope: policyScope,
        employmentId: policyScope === 'employment' ? Number(policyEmploymentId) : null,
        positionId: policyScope === 'position' ? Number(policyPositionId) : null,
        departmentId: policyScope === 'department' ? Number(policyDepartmentId) : null,
        effectiveYear: year,
        effectiveMonth: monthNumber,
        calculationMethod: policyMethod,
        fixedMonthlyRate: Number(policyFixedRate),
        revenuePercent: Number(policyRevenuePercent),
        hourlyRateOverride: policyHourlyRate === '' ? null : Number(policyHourlyRate),
        evaluationCoefficientOverride: policyCoefficient === '' ? null : Number(policyCoefficient),
    });
  };

  const saveSalaryRule = (event: FormEvent) => {
    event.preventDefault();
    void reviewRuleChange('rate', {
        positionId: Number(salaryRulePositionId),
        effectiveYear: year,
        effectiveMonth: monthNumber,
        baseRate: Number(salaryRuleRate),
    });
  };
  const confirmRuleChange = () => {
    if (!ruleChangeReview) return;
    const reviewed = ruleChangeReview;
    void runAction(async () => {
      try {
        await api.put(`/restaurant-payroll/${reviewed.kind === 'policy' ? 'policies' : 'salary-rules'}`,
          { ...reviewed.payload, impactVersion: reviewed.impact.version });
      } finally {
        if (mounted.current && currentMonthRef.current === month) setRuleChangeReview(null);
      }
    }, 'Правило збережено новою ревізією. Перевірте оновлений розрахунок перед погодженням.');
  };

  const reviewRetirement = async (kind: 'policy' | 'rate', item: { id: number; version: number }) => {
    if (!retirementReason.trim() || busy || !canEdit) return;
    const version = requestVersion.current;
    const isCurrent = () => mounted.current && currentMonthRef.current === month && requestVersion.current === version;
    setBusy(true);
    setError('');
    setNotice('');
    setRetirementReview(null);
    try {
      const { data } = await api.get<PayrollOptions>('/restaurant-payroll/options', {
        params: { year, month: monthNumber, retirementVersion: item.version,
          ...(kind === 'policy' ? { retirePolicyId: item.id } : { retireRateId: item.id }) },
      });
      if (!isCurrent()) return;
      const impact = data.retirementImpact;
      if (!impact || impact.kind !== kind || impact.id !== item.id || impact.ruleVersion !== item.version || !impact.version)
        throw new Error('Сервер не підтвердив наслідки припинення. Оновіть зарплату та повторіть перегляд.');
      setOptions(data);
      setRetirementReview({ ...impact, reason: retirementReason.trim() });
    } catch (reviewError) {
      if (isCurrent()) setError(messageFrom(reviewError, 'Не вдалося перевірити наслідки припинення правила.'));
    } finally {
      if (isCurrent()) setBusy(false);
    }
  };

  const confirmRetirement = () => {
    if (!retirementReview || retirementReview.reason !== retirementReason.trim()) return;
    void runAction(async () => {
      try {
        await api.post(`/restaurant-payroll/${retirementReview.kind === 'policy' ? 'policies' : 'salary-rules'}/${retirementReview.id}/retire`, {
          version: retirementReview.ruleVersion,
          effectiveYear: year,
          effectiveMonth: monthNumber,
          reason: retirementReview.reason,
          impactVersion: retirementReview.version,
        });
      } finally {
        if (mounted.current && currentMonthRef.current === month) setRetirementReview(null);
      }
    }, 'Правило припинено з вибраного місяця; попередня історія збережена.');
  };

  const saveDraftInput = (event: FormEvent) => {
    event.preventDefault();
    if (!period?.exists || !selectedEmploymentId) return;
    const values = draftInputs[Number(selectedEmploymentId)] ?? {
      fixedBonus: '0', bonusPercent: '0', fixedPenalty: '0', penaltyPercent: '0',
    };
    void runAction(async () => {
      await api.put(`/restaurant-payroll/${year}/${monthNumber}/employees/${selectedEmploymentId}/adjustments`, {
        version: period.version,
        fixedBonus: Number(values.fixedBonus),
        bonusPercent: Number(values.bonusPercent),
        fixedPenalty: Number(values.fixedPenalty),
        penaltyPercent: Number(values.penaltyPercent),
      });
    }, 'Місячні бонуси та штрафи збережено в чернетці.');
  };

  const transition = (action: 'approve' | 'close') => {
    if (!period?.exists) return;
    if (action === 'approve' && !period.previewVersion) {
      setError('Не вдалося підтвердити переглянутий розрахунок. Оновіть зарплату перед погодженням.');
      return;
    }
    if (action === 'close' && !window.confirm(
      'Закрити період? Після закриття скасування погодження недоступне. Зміни можливі лише окремими коригуваннями зі збереженням первинного знімка.')) return;
    const warnings = period.openAttendanceWarnings ?? [];
    if (action === 'approve' && warnings.length > 0 && !window.confirm(
      `Є ${warnings.reduce((sum, item) => sum + item.openIntervals, 0)} незавершених інтервалів у ${warnings.length} працівників. Їхні години не враховано в нарахуванні. Погодити зарплату без цих годин?`)) return;
    void runAction(async () => {
      await api.post(`/restaurant-payroll/${year}/${monthNumber}/${action}`, { version: period.version,
        ...(action === 'approve' ? { previewVersion: period.previewVersion } : {}),
        ...(action === 'approve' && warnings.length > 0 ? { openAttendanceConfirmation: period.openAttendanceVersion } : {}) });
    }, action === 'approve' ? 'Зарплату ресторану погоджено та зафіксовано.' : 'Період ресторану закрито.');
  };

  const cancelApproved = (event: FormEvent) => {
    event.preventDefault();
    if (!period?.exists || period.status !== 'approved' || !cancellationReason.trim()) return;
    if (!window.confirm(`Скасувати погодження зарплати за ${month}? Причина: ${cancellationReason.trim()}. ` +
      'Первинний знімок залишиться в історії; буде створена нова чернетка з преміями й утриманнями.')) return;
    void runAction(async () => {
      await api.post(`/restaurant-payroll/${year}/${monthNumber}/cancel`, {
        version: period.version,
        reason: cancellationReason.trim(),
      });
      setCancellationReason('');
    }, 'Погоджений період скасовано зі збереженням первинного знімка та аудиту.');
  };

  const addAdjustment = (event: FormEvent) => {
    event.preventDefault();
    if (!period?.exists || !adjustmentEmploymentId) return;
    const key = adjustmentKey || requestKey();
    setAdjustmentKey(key);
    void runAction(async () => {
      await api.post(`/restaurant-payroll/${year}/${monthNumber}/adjustments`, {
        employmentId: Number(adjustmentEmploymentId),
        amount: Number(adjustmentAmount),
        reason: adjustmentReason,
      }, { headers: { 'Idempotency-Key': key } });
      if (!mounted.current || currentMonthRef.current !== month) return;
      setAdjustmentAmount('');
      setAdjustmentReason('');
      setAdjustmentKey('');
    }, 'Коригування додано окремим записом.');
  };

  const createDraft = () => {
    void runAction(async () => {
      await api.post(`/restaurant-payroll/${year}/${monthNumber}`, {});
    }, 'Чернетку створено. Перевірте розрахунок перед погодженням.');
  };

  const statusText = period?.status === 'approved' ? 'Погоджено' :
    period?.status === 'closed' ? 'Закрито' :
      period?.status === 'cancelled' ? 'Скасовано' : 'Чернетка';
  const persistDisclosure = (key: DisclosureKey, open: boolean) => {
    setDisclosures(previous => {
      const next = { ...previous, [key]: open };
      localStorage.setItem(DISCLOSURE_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };
  const onDisclosureToggle = (key: DisclosureKey) => (event: React.SyntheticEvent<HTMLDetailsElement>) => {
    if (disclosures[key] !== event.currentTarget.open) persistDisclosure(key, event.currentTarget.open);
  };
  const revealHalls = () => {
    const section = hallsDisclosure.current;
    if (!section) return;
    section.open = true;
    persistDisclosure('halls', true);
    section.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  const sortedEmployees = [...(period?.employees ?? [])].sort((a, b) => {
    const amount = employeeSort === 'amount-desc' ? b.amount - a.amount : employeeSort === 'amount-asc' ? a.amount - b.amount : 0;
    const department = employeeSort === 'department' ? (a.departmentName || '').localeCompare(b.departmentName || '', 'uk') : 0;
    return amount || department || a.employeeName.localeCompare(b.employeeName, 'uk') || a.employmentId - b.employmentId;
  });
  const showActions = () => {
    const section = document.getElementById('restaurant-payroll-approval');
    section?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    section?.focus();
  };

  return (
    <main className="salary-payroll salary-payroll--restaurant">
      <header className="salary-payroll__header">
        <div>
          <h1>Зарплата та виторг ресторану</h1>
          <p>Дані належать лише вибраній операційній групі. Персонал, виторг, правила і періоди інших ресторанів не використовуються.</p>
        </div>
        <label>Місяць<input aria-label="Місяць ресторанної зарплати" type="month" min="2000-01" max="2200-12" value={month} onChange={event => selectMonth(event.target.value)} />
          <small>Цей браузер запам’ятовує лише місяць, без сум або персональних даних.</small></label>
      </header>
      {error && <p role="alert" className="salary-payroll__error">{error}</p>}
      {notice && <p role="status" className="salary-payroll__notice">{notice}</p>}
      {busy && <p role="status">Оновлення ресторанного періоду…</p>}
      {period && options && period.year === year && period.month === monthNumber && <>
        <div className="salary-payroll__context-bar">
        <p>Група №{groupId} · {month} · {statusText} · блокерів: {period.issues.length}
          {' · '}попереджень табеля: {period.openAttendanceWarnings?.length ?? 0}
          {period.issues.length > 0 && <>{' '}<a href="#restaurant-payroll-blockers">До блокерів</a></>}</p>
        <nav className="salary-payroll__workflow" aria-label="Шлях підготовки зарплати">
          {canViewPersonnel ? <a href={`/management/employees?view=attendance&month=${month}`}>1. Табель</a> : <span>1. Табель (немає доступу)</span>}
          <a href="#restaurant-payroll-review">2. Перевірка</a>
          <a href="#restaurant-payroll-approval" onClick={showActions}>3. Погодження</a>
          {canViewPersonnel ? <a href={`/management/employees?view=attendance&month=${month}#restaurant-monthly-reports`}>4. Звіти</a> : <span>4. Звіти (немає доступу)</span>}
        </nav>
        </div>
        {personnelAccessNotice && <p role="status">{personnelAccessNotice}</p>}
        <section className="salary-payroll__summary" aria-label="Контекст і готовність зарплати">
          <p><strong>Операційна група:</strong> №{groupId} · <strong>Місяць:</strong> {month}</p>
          <p><strong>Статус:</strong> {statusText}</p>
          {period.exists && <p><strong>Редакція:</strong> {period.revision}</p>}
          <p><strong>Власний виторг:</strong> {currency.format(period.monthlyRevenue)}</p>
          <p><strong>Нараховано:</strong> {currency.format(period.total)}</p>
          <p><strong>Готовність:</strong> {period.issues.length} блокерів · {period.openAttendanceWarnings?.length ?? 0} працівників з незавершеним табелем.
            {period.issues.length > 0 && <>{' '}<a href="#restaurant-payroll-blockers">Переглянути блокери</a></>}</p>
          <p>{period.status === 'draft' ? 'Попередній розрахунок — не фіналізована зарплата.' : 'Збережений стан періоду; історичні суми не перераховуються.'}</p>
          {period.status === 'draft' && <p id="payroll-employment-history-warning" role="note">
            Дата зміни посади чи підрозділу окремо не зберігається. Поточні значення діють для всього запису працевлаштування
            та можуть змінити розрахунок незатверджених минулих місяців навіть без блокерів.
            Перед погодженням перевірте посади, підрозділи та джерела ставок за обраний місяць.
            Уже погоджені та закриті знімки зарплати залишаються незмінними.
            Для переходу з початку місяця за потреби оформіть окремі послідовні працевлаштування без перекриття дат;
            перехід серед місяця без окремо погодженої формули блокується, автоматичної пропорції немає.
          </p>}
          {period.salaryFundPercent > 0 && <>
            <p><strong>Фонд:</strong> {currency.format(period.salaryFund)} ({formatRestaurantPayrollDecimal(period.salaryFundPercent, 4)}%)</p>
            <p><strong>Зважені години:</strong> {formatRestaurantPayrollDecimal(period.totalSalaryHours, 2)} · {currency.format(period.hourRate)} / год.</p>
          </>}
          {period.approvedByUsername && <p>Погодив(-ла): {period.approvedByUsername}{period.approvedAtUtc && ` · ${new Date(period.approvedAtUtc).toLocaleString('uk-UA')}`}</p>}
          {period.closedByUsername && <p>Закрив(-ла): {period.closedByUsername}{period.closedAtUtc && ` · ${new Date(period.closedAtUtc).toLocaleString('uk-UA')}`}</p>}
          {period.status === 'approved' && <p><strong>Наступна дія:</strong> перевірте збережений знімок і звіти. До закриття погодження можна скасувати з причиною; закриття заборонить скасування.</p>}
          {period.status === 'closed' && <p><strong>Наступна дія:</strong> звіти зі збереженого знімка. Виправлення — лише окремим коригуванням; історичний знімок і табель не переписуються.</p>}
          <button type="button" disabled={busy || comparing || (year === 2000 && monthNumber === 1)}
            onClick={() => void comparePreviousPeriod()}>Порівняти з попереднім місяцем</button>
          {comparing && <p role="status">Читання попереднього місяця…</p>}
          {comparisonNotice && <p role="status">{comparisonNotice}</p>}
          {previousPeriod && <section aria-label="Порівняння фіналізованого місяця">
            <p>{previousPeriod.year}-{String(previousPeriod.month).padStart(2, '0')} · {previousPeriod.status === 'closed' ? 'Закрито' : 'Погоджено'} · {currency.format(previousPeriod.total)}</p>
            <p>Різниця загальних сум: {currency.format(sumDisplayedMoney([period.total, -previousPeriod.total]))}.
              Це порівняння показаних підсумків, не прогноз і не пояснення причин; склад працівників та коригування можуть відрізнятися.
              {period.status === 'draft' && ' Поточний місяць — лише попередній розрахунок.'}</p>
          </section>}
          {period.status === 'cancelled' && <section aria-label="Аудит скасування">
            <h2>Погоджену зарплату скасовано</h2>
            <p><strong>Причина:</strong> {period.cancellationReason}</p>
            <p>{period.cancelledByUsername} · {period.cancelledAtUtc ? new Date(period.cancelledAtUtc).toLocaleString('uk-UA') : ''}</p>
            <p>Первинний знімок збережено без змін. Створіть нову редакцію чернетки, щоб повторно розрахувати цей місяць.</p>
          </section>}
        </section>
        {(period.cancellationHistory?.length ?? 0) > 0 && <details className="salary-payroll__disclosure">
          <summary>Скасовані редакції зі збереженими знімками ({period.cancellationHistory?.length ?? 0})</summary>
          <section className="salary-payroll__policy-settings">
            {period.cancellationHistory?.map(item => <article key={item.revision}>
              <h2>Редакція {item.revision} · скасовано</h2>
              <p><strong>Причина:</strong> {item.cancellationReason}</p>
              <p>{item.cancelledByUsername} · {new Date(item.cancelledAtUtc).toLocaleString('uk-UA')} · первинна сума {currency.format(item.totalSnapshot)}</p>
              <ul>{item.employees.map((employee, index) => <li key={`${employee.employeeName}-${index}`}>
                {employee.employeeName} · {employee.positionSnapshot || '—'} · {currency.format(employee.amount)}
              </li>)}</ul>
            </article>)}
          </section>
        </details>}

        {period.issues.length > 0 && <section id="restaurant-payroll-blockers" className="salary-payroll__issues" aria-label="Проблеми розрахунку">
          <h2>Потрібно виправити перед погодженням</h2>
          <ul>{period.issues.map((issue, index) => <li key={`${issue}-${index}`}>{issue}</li>)}</ul>
          {(period.readinessTargets?.length ?? 0) > 0 && <>
            <p>Доступні переходи за визначеними джерелами блокерів (виправлення не виконуються автоматично):</p>
            <ul>{period.readinessTargets?.map((target, index) => <li key={index}>
              {target.kind === 'employment' && target.employmentId && (canViewPersonnel ? <a
                href={`/management/employees?employmentId=${target.employmentId}&month=${month}`}>{target.label}</a>
                : <span>{target.label} — потрібен кадровий доступ цього ресторану.</span>)}
              {target.kind === 'rules' && <a href="#restaurant-payroll-rules"
                onClick={() => persistDisclosure('policies', true)}>{target.label}</a>}
              {target.kind === 'revenue' && <a href="#restaurant-payroll-revenue"
                onClick={() => persistDisclosure('revenue', true)}>{target.label}</a>}
              {target.kind === 'halls' && <a href="#restaurant-payroll-halls"
                onClick={revealHalls}>{target.label}</a>}
            </li>)}</ul>
          </>}
        </section>}
        {(period.openAttendanceWarnings?.length ?? 0) > 0 && <section className="salary-payroll__issues" aria-label="Попередження готовності табеля">
          <h2>Незавершений табель — години не враховано</h2>
          <ul>{period.openAttendanceWarnings?.map(item => <li key={item.employmentId}>
            {item.employeeName}: незавершених інтервалів — {item.openIntervals}.
            {canViewPersonnel && <>{' '}<a href={`/management/employees?employmentId=${item.employmentId}&month=${month}&section=attendance`}>Інтервали цього працівника</a></>}
          </li>)}</ul>
          <p>Працівник лише з відкритими інтервалами може не входити до розрахунку. Погодження без цих годин потребує окремого підтвердження.</p>
          {canViewPersonnel && <a href={`/management/employees?view=attendance&month=${month}`}>Перевірити табель за {String(monthNumber).padStart(2, '0')}.{year}</a>}
        </section>}
        <section id="restaurant-payroll-review" aria-label="Перевірка розрахунку працівників">
        <h2>Перевірка працівників</h2>
        <p>Розгорніть працівника, щоб перевірити джерело правила й складові. Сортування змінює лише порядок показу, не суми або пріоритет правил.</p>
        <label>Порядок працівників<select value={employeeSort} onChange={event => setEmployeeSort(event.target.value)}>
          <option value="name">За ім’ям</option><option value="department">За підрозділом</option>
          <option value="amount-desc">За сумою: спочатку найбільші</option>
          <option value="amount-asc">За сумою: спочатку найменші</option>
        </select></label>
        <div className="salary-payroll__table-wrap">
          <table>
            <thead><tr><th>Працівник ресторану</th><th>Підрозділ / посада</th><th>Години</th><th>Ставка / коефіцієнт</th><th>База</th><th>Бонус</th><th>Штраф</th><th>Разом</th></tr></thead>
            <tbody>{sortedEmployees.map(item => <tr key={item.employmentId}>
              <td><strong>{item.employeeName}</strong>
                <button type="button" disabled={busy} aria-controls="payroll-private-delivery"
                  aria-expanded={deliveryEmploymentId === item.employmentId}
                  onClick={() => {
                    pendingReportFocus.current = deliveryEmploymentId === item.employmentId ? null : 'delivery';
                    setDeliveryEmploymentId(current => current === item.employmentId ? null : item.employmentId);
                  }}>
                  {deliveryEmploymentId === item.employmentId ? 'Приховати приватну відправку' : 'Підготувати приватну відправку'}
                </button>
                <button type="button" disabled={busy} aria-controls="payroll-statement-print"
                  aria-expanded={printEmploymentId === item.employmentId}
                  onClick={() => {
                    pendingReportFocus.current = printEmploymentId === item.employmentId ? null : 'print';
                    setPrintEmploymentId(current => current === item.employmentId ? null : item.employmentId);
                  }}>
                  {printEmploymentId === item.employmentId ? 'Приховати друк/PDF' : 'Друк/PDF виписки'}
                </button>
                <details className="salary-payroll__employee-detail">
                  <summary>Деталі розрахунку · {item.employeeName}</summary>
                  <p>{calculationMethodLabels[item.method] ?? (item.method === 'halls-fund' ? 'Окремий фонд HALLS' : item.method)}</p>
                  {item.method !== 'halls-fund' && <><p>Правило: {policyScopeLabel(item.policySource, options)}
                    {item.policyEffectiveYear > 0 && ` · ${String(item.policyEffectiveMonth).padStart(2, '0')}.${item.policyEffectiveYear}`}.</p>
                  <p>Параметри беруться з вибраного цілого правила; нижчі рівні не змішуються. Параметри, не перевизначені правилом, використовують локальний профіль.</p></>}
                  <dl>
                    {item.method === 'hourly-coefficient' && <><dt>Погодинна ставка</dt><dd>{currency.format(item.hourlyRate)}</dd></>}
                    {item.method === 'fixed-plus-revenue' && <>
                      <dt>Фіксована місячна ставка</dt><dd>{currency.format(item.fixedMonthlyRate)}</dd>
                      <dt>Частка локального виторгу</dt><dd>{formatRestaurantPayrollDecimal(item.revenuePercent, 4)}% · джерело {currency.format(item.monthlyRevenue)}</dd>
                    </>}
                    <dt>Завершені години</dt><dd>{formatRestaurantPayrollDecimal(item.workedHours, 4)}</dd>
                    <dt>Розрахункові години / вага</dt><dd>{formatRestaurantPayrollDecimal(item.salaryHours, 4)}</dd>
                    <dt>Коефіцієнт оцінювання</dt><dd>{formatRestaurantPayrollDecimal(item.evaluationCoefficient, 4)} · {item.evaluationCoefficientSource === 'manual' ? 'Із вибраного правила' : item.evaluationCoefficientSource === 'HR' ? 'Оцінювання HR' : item.evaluationCoefficientSource || 'Джерело не зазначене'}</dd>
                    <dt>Базове нарахування</dt><dd>{currency.format(item.baseSalary)}</dd>
                    <dt>Бонус</dt><dd>{currency.format(item.fixedBonus)} + {formatRestaurantPayrollDecimal(item.bonusPercent, 4)}% · разом {currency.format(item.bonus)}</dd>
                    <dt>Утримання</dt><dd>{currency.format(item.fixedPenalty)} + {formatRestaurantPayrollDecimal(item.penaltyPercent, 4)}% · разом {currency.format(item.penalty)}</dd>
                    <dt>Сума рядка</dt><dd>{currency.format(item.amount)}</dd>
                  </dl>
                  {item.method === 'halls-fund' && <p>HALLS має окремі джерела й розподіл; звичайна ієрархія правил його не замінює.</p>}
                  {canViewPersonnel && <a href={`/management/employees?employmentId=${item.employmentId}&month=${month}&section=attendance`}>Перевірити табель працівника</a>}
                  <p>{canViewPersonnel
                    ? 'Особисту виписку можна підготувати тут; деталі чужого табеля доступні лише з HR-дозволом.'
                    : 'Табель, картки та звіти персоналу потребують окремого дозволу перегляду й локального кадрового доступу.'}</p>
                </details>
              </td><td>{item.departmentName || '—'} / {item.position || '—'}</td>
              <td>{formatRestaurantPayrollDecimal(item.workedHours, 2)}{item.method === 'revenue-weighted-hours' || item.method === 'halls-fund' ? ` / ${formatRestaurantPayrollDecimal(item.salaryHours, 2)}` : ''}</td>
              <td>{item.method === 'hourly-coefficient' ? `${currency.format(item.hourlyRate)} × ${formatRestaurantPayrollDecimal(item.evaluationCoefficient, 4)}` :
                item.method === 'halls-fund' ? `HALLS · коеф. ${formatRestaurantPayrollDecimal(item.evaluationCoefficient, 4)}` :
                item.method === 'revenue-weighted-hours' ? `коеф. ${formatRestaurantPayrollDecimal(item.evaluationCoefficient, 4)}` :
                  `${currency.format(item.fixedMonthlyRate)} + ${formatRestaurantPayrollDecimal(item.revenuePercent, 4)}%`}
                <small className="salary-payroll__rule-source">
                  Джерело: {item.policySource === 'employment-default' ? 'профіль працевлаштування' : policyScopeLabel(item.policySource, options)}
                  {item.policyEffectiveYear > 0 && ` · чинне з ${String(item.policyEffectiveMonth).padStart(2, '0')}.${item.policyEffectiveYear}`}
                </small>
              </td>
              <td>{currency.format(item.baseSalary)}</td><td>{currency.format(item.bonus)}</td><td>{currency.format(item.penalty)}</td><td>{currency.format(item.amount)}</td>
              </tr>
            )}
            {!period.employees.length && <tr><td colSpan={8}>За цей період немає працівників для розрахунку.</td></tr>}
              <tr className="salary-payroll__table-total"><th scope="row">Разом</th><td>—</td><td>—</td><td>—</td>
                <td>{currency.format(sumDisplayedMoney(period.employees.map(item => item.baseSalary)))}</td>
                <td>{currency.format(sumDisplayedMoney(period.employees.map(item => item.bonus)))}</td>
                <td>{currency.format(sumDisplayedMoney(period.employees.map(item => item.penalty)))}</td>
                <td>{currency.format(period.total)}</td></tr>
            </tbody>
          </table>
        </div>
        {sortedEmployees.filter(item => item.employmentId === deliveryEmploymentId).map(item =>
          <div id="payroll-private-delivery" key={`delivery-${item.employmentId}`}>
            <h3 ref={deliveryHeading} tabIndex={-1}>Приватна відправка · {item.employeeName}</h3>
            <PersonalReportDeliveryPanel template="employee-statement" year={period.year} month={period.month}
              employmentId={item.employmentId} scopeKey={groupId} />
          </div>)}
        {sortedEmployees.filter(item => item.employmentId === printEmploymentId).map(item =>
          <div id="payroll-statement-print" key={`print-${item.employmentId}`}>
            <h3 ref={printHeading} tabIndex={-1}>Друк/PDF · {item.employeeName}</h3>
            <PersonalEmployeeStatementPrint year={period.year} month={period.month}
              employmentId={item.employmentId} scopeKey={groupId} />
          </div>)}
        </section>
        <div id="restaurant-payroll-approval" tabIndex={-1} className="salary-payroll__actions" aria-label="Дії із зарплатним періодом">
          {canEdit && (!period.exists || period.status === 'cancelled') &&
            <button type="button" disabled={busy} onClick={createDraft}>Створити чернетку{period.status === 'cancelled' ? ' нової редакції' : ''}</button>}
          {canEdit && period.exists && period.status === 'draft' && <>
            <p>Перед погодженням врахуйте вплив поточної посади й підрозділу на минулі чернетки; перевірте попередження в огляді розрахунку.</p>
            <button type="button" aria-describedby="payroll-employment-history-warning" disabled={busy || period.issues.length > 0 || !period.employees.length} onClick={() => transition('approve')}>Погодити й зафіксувати</button>
            {(busy || period.issues.length > 0 || !period.employees.length) && <span className="salary-payroll__approval-reason" role="status">
              {busy ? 'Триває збереження' : period.issues.length > 0 ? `Є ${period.issues.length} проблем` : 'Немає працівників у розрахунку'}
            </span>}
            <button type="button" className="salary-payroll__secondary-action" aria-controls="restaurant-payroll-halls" onClick={revealHalls}>Перевірити фонд HALLS</button>
          </>}
          {canEdit && period.status === 'approved' && <>
            <button type="button" disabled={busy} onClick={() => transition('close')}>Закрити період</button>
            <form className="salary-payroll__adjustment" onSubmit={cancelApproved}>
              <label>Причина скасування погодження
                <input required maxLength={1000} value={cancellationReason}
                  onChange={event => setCancellationReason(event.target.value)} />
              </label>
              <button type="submit" className="salary-payroll__secondary-action"
                disabled={busy || !cancellationReason.trim()}>Скасувати погоджену зарплату</button>
            </form>
          </>}
        </div>

        <details ref={hallsDisclosure} id="restaurant-payroll-halls" className="salary-payroll__disclosure salary-payroll__disclosure--halls"
          open={disclosures.halls} onToggle={onDisclosureToggle('halls')}>
          <summary>Фонд HALLS цієї групи — перевірка й налаштування</summary>
          <RestaurantHallsPayrollPanel groupId={groupId} year={year} month={monthNumber} version={period.version}
            exists={period.exists} status={period.status} canEdit={canEdit} busy={busy}
            employees={options.employees} review={period.halls} runAction={runAction} />
        </details>

        <details id="restaurant-payroll-revenue" className="salary-payroll__disclosure" open={disclosures.revenue} onToggle={onDisclosureToggle('revenue')}>
          <summary>Місячний виторг ресторану</summary>
          <section className="salary-payroll__policy-settings">
          <p>Виторг є приватним записом вибраної групи; запис HALLS або іншого ресторану не є резервним джерелом.</p>
          {canEdit && period.status === 'draft' && <form onSubmit={saveRevenue}>
            <label>Виторг, ₴<input required type="number" min="0" step="0.01" value={revenueAmount} onChange={event => setRevenueAmount(event.target.value)} /></label>
            <label>Примітка<input maxLength={2000} value={revenueNotes} onChange={event => setRevenueNotes(event.target.value)} /></label>
            <button type="submit" disabled={busy}>Зберегти виторг</button>
          </form>}

          </section>
        </details>

        <details id="restaurant-payroll-rules" className="salary-payroll__disclosure" open={disclosures.policies} onToggle={onDisclosureToggle('policies')}>
          <summary>Правила нарахування й ставки</summary>
          <section className="salary-payroll__policy-settings">
          <p>Ціле правило вибирається за пріоритетом: особисте → посада → підрозділ → ресторан. Параметри нижчого рівня не змішуються з вибраним правилом; у розрахунку видно джерело та ефективний місяць.</p>
          <p>Спочатку перегляньте вплив нового правила або ставки. Збереження виконується лише окремим підтвердженням після серверної перевірки джерел.</p>
          {canEdit && period.status === 'draft' && <form onSubmit={savePolicy} onChangeCapture={invalidateRuleChange}>
            <label>Рівень правила
              <select value={policyScope} onChange={event => {
                const next = event.target.value as typeof policyScope;
                setPolicyScope(next);
                if (next !== 'global' && policyMethod === 'revenue-weighted-hours')
                  setPolicyMethod('hourly-coefficient');
              }}>
                <option value="global">Ресторан</option>
                <option value="department">Підрозділ</option>
                <option value="position">Посада</option>
                <option value="employment">Особисте працевлаштування</option>
              </select>
            </label>
            {policyScope === 'employment' && <label>Працівник
              <select required value={policyEmploymentId} onChange={event => setPolicyEmploymentId(event.target.value)}>
                <option value="">Оберіть працівника</option>
                {options.employees.map(employee => <option key={employee.employmentId} value={employee.employmentId}>{employee.fullName}</option>)}
              </select>
            </label>}
            {policyScope === 'position' && <label>Посада
              <select required value={policyPositionId} onChange={event => setPolicyPositionId(event.target.value)}>
                <option value="">Оберіть посаду</option>
                {options.positions.map(position => <option key={position.id} value={position.id}>{position.name}</option>)}
              </select>
            </label>}
            {policyScope === 'department' && <label>Підрозділ
              <select required value={policyDepartmentId} onChange={event => setPolicyDepartmentId(event.target.value)}>
                <option value="">Оберіть підрозділ</option>
                {options.departments.map(department => <option key={department.departmentId} value={department.departmentId}>{department.name}</option>)}
              </select>
            </label>}
            <label>Метод
              <select value={policyMethod} onChange={event => setPolicyMethod(event.target.value as PayrollEmployee['method'])}>
                <option value="hourly-coefficient">Години × приватна ставка × коефіцієнт</option>
                <option value="fixed-plus-revenue">Фіксована сума + частка виторгу</option>
                {policyScope === 'global' && <option value="revenue-weighted-hours">Власний фонд від виторгу ÷ зважені години</option>}
              </select>
            </label>
            {policyMethod === 'fixed-plus-revenue' && <>
              <label>Фіксована сума, ₴<input type="number" min="0" step="0.01" value={policyFixedRate} onChange={event => setPolicyFixedRate(event.target.value)} /></label>
              <label>Відсоток виторгу<input type="number" min="0" max="100" step="0.0001" value={policyRevenuePercent} onChange={event => setPolicyRevenuePercent(event.target.value)} /></label>
            </>}
            {policyMethod === 'revenue-weighted-hours' && <label>Відсоток фонду<input type="number" min="0" max="100" step="0.0001" value={policyRevenuePercent} onChange={event => setPolicyRevenuePercent(event.target.value)} /></label>}
            {policyMethod === 'hourly-coefficient' && <label>Ставка, ₴ (порожньо — базова ставка працевлаштування)<input type="number" min="0" step="0.01" value={policyHourlyRate} onChange={event => setPolicyHourlyRate(event.target.value)} /></label>}
            <label>Коефіцієнт (порожньо — локальне HR-оцінювання)<input type="number" min="0" step="0.000001" value={policyCoefficient} onChange={event => setPolicyCoefficient(event.target.value)} /></label>
            <button type="submit" disabled={busy}>Переглянути вплив правила з {String(monthNumber).padStart(2, '0')}.{year}</button>
          </form>}
          {canEdit && period.status === 'draft' && <form onSubmit={saveSalaryRule} onChangeCapture={invalidateRuleChange}>
            <h3 className="salary-payroll__form-heading">Посадове правило: погодинна ставка</h3>
            <label>Посада з каталогу<select required value={salaryRulePositionId}
              onChange={event => setSalaryRulePositionId(event.target.value)}>
              <option value="">Оберіть посаду</option>
              {options.positions.map(position => <option key={position.id} value={position.id}>{position.name}</option>)}
            </select></label>
            <label>Ставка, ₴<input required type="number" min="0" step="0.01" value={salaryRuleRate} onChange={event => setSalaryRuleRate(event.target.value)} /></label>
            <button type="submit" disabled={busy}>Переглянути вплив ставки з {String(monthNumber).padStart(2, '0')}.{year}</button>
          </form>}
          {ruleChangeReview && <section ref={ruleChangePanel} tabIndex={-1} id="restaurant-payroll-rule-change-review" aria-label="Вплив нового правила" className="salary-payroll__rule-review">
            <h3>Перевірте нову ревізію правила · {month}</h3>
            <p>{ruleChangeReview.kind === 'rate'
              ? `${policyScopeLabel(`position:${ruleChangeReview.payload.positionId}`, options)} · ставка ${currency.format(ruleChangeReview.payload.baseRate ?? 0)}`
              : `${policyScopeLabel(ruleChangeReview.payload.scope === 'employment' ? `employment:${ruleChangeReview.payload.employmentId}`
                : ruleChangeReview.payload.scope === 'position' ? `position:${ruleChangeReview.payload.positionId}`
                  : ruleChangeReview.payload.scope === 'department' ? `department:${ruleChangeReview.payload.departmentId}` : 'global', options)}
                · ${calculationMethodLabels[ruleChangeReview.payload.calculationMethod ?? '']}`}
            </p>
            {ruleChangeReview.kind === 'policy' && <p>
              Фіксована ставка: {currency.format(ruleChangeReview.payload.fixedMonthlyRate ?? 0)} · відсоток: {formatRestaurantPayrollDecimal(ruleChangeReview.payload.revenuePercent ?? 0, 4)}% ·
              погодинна ставка: {ruleChangeReview.payload.hourlyRateOverride == null ? 'локальний профіль' : currency.format(ruleChangeReview.payload.hourlyRateOverride)} ·
              коефіцієнт: {ruleChangeReview.payload.evaluationCoefficientOverride == null ? 'локальний профіль' : formatRestaurantPayrollDecimal(ruleChangeReview.payload.evaluationCoefficientOverride, 4)}
            </p>}
            <p>Це перегляд джерел, не прогноз сум і не підтвердження готовності.
              Після збереження перевірте оновлений розрахунок; фіналізовані знімки не змінюються.</p>
            <p>Змінюється джерело/ревізія для {ruleChangeReview.impact.employees.length} працевлаштувань;
              без зміни джерела: {ruleChangeReview.impact.unchangedEmployments}; окремий HALLS: {ruleChangeReview.impact.hallsEmployments}.</p>
            <ul>{ruleChangeReview.impact.employees.map(item => <li key={item.employmentId}>
              <strong>{item.fullName}</strong>: {policyScopeLabel(item.beforeSource, options)} → {policyScopeLabel(item.afterSource, options)}
              {item.beforeSource === item.afterSource && ' · нова ревізія на тому самому рівні або збережений блокер'}
            </li>)}</ul>
            <p>Вищі особисті/посадові правила можуть залишити працівника на попередньому джерелі. Блокери й сумісність фондів перевіряються в розрахунку, а не цим списком.</p>
            <button type="button" disabled={busy} onClick={confirmRuleChange}>Підтвердити збереження правила</button>
            <button type="button" disabled={busy} className="salary-payroll__secondary-action" onClick={invalidateRuleChange}>Не зберігати правило</button>
          </section>}
          <div className="salary-payroll__policy-list">
            <h3>Історія правил</h3>
            <p>Стан на {String(monthNumber).padStart(2, '0')}.{year} у межах кожного рівня. Фактичне джерело для працівника — у таблиці розрахунку.
              Замінені ревізії збережені для історії; їх припинення не вимикає поточне правило.</p>
            <p>Охоплення показує вибір джерела за ієрархією для місяця, а не підтвердження готовності чи суми ЗП.
              Учасники HALLS виключені: для них діє окреме джерело.</p>
            {canEdit && period.status === 'draft' && <label>Причина припинення правила
              <input maxLength={1000} value={retirementReason} disabled={busy}
                onChange={event => {
                  setRetirementReason(event.target.value);
                  setRetirementReview(null);
                }} />
            </label>}
            {options.policies.map(item => <div key={item.id}>
              <p><strong>{item.state ? ruleStateLabels[item.state] : 'Стан правила не підтверджено'}</strong> · {policyScopeLabel(item.scopeKey, options)} · набуття чинності {String(item.effectiveMonth).padStart(2, '0')}.{item.effectiveYear} · v{item.revision} · {calculationMethodLabels[item.calculationMethod] ?? item.calculationMethod}
                {item.retiredEffectiveYear ? ` · припинене з ${String(item.retiredEffectiveMonth).padStart(2, '0')}.${item.retiredEffectiveYear}` : ''}
              </p>
              <RuleCoverageDetails coverage={item.coverage} />
              {canEdit && period.status === 'draft' && canRetireRule(item.state) && !item.retiredEffectiveYear &&
                (year > item.effectiveYear || (year === item.effectiveYear && monthNumber >= item.effectiveMonth)) &&
                <button type="button" className="salary-payroll__secondary-action" disabled={busy || !retirementReason.trim()}
                  onClick={() => void reviewRetirement('policy', item)}>Припинити з {String(monthNumber).padStart(2, '0')}.{year}</button>}
            </div>)}
            {options.salaryRules.map(item => <div key={`rate-${item.id}`}>
              <p><strong>{item.state ? ruleStateLabels[item.state] : 'Стан правила не підтверджено'}</strong> · {item.positionId == null ? `Історична непов’язана ставка «${item.position}»: ${currency.format(item.baseRate)} · не застосовується автоматично` : `${policyScopeLabel(`position:${item.positionId}`, options)}: ${currency.format(item.baseRate)}`} · набуття чинності {String(item.effectiveMonth).padStart(2, '0')}.{item.effectiveYear} · v{item.revision}
                {item.retiredEffectiveYear ? ` · припинене з ${String(item.retiredEffectiveMonth).padStart(2, '0')}.${item.retiredEffectiveYear}` : ''}
              </p>
              <RuleCoverageDetails coverage={item.coverage} />
              {canEdit && period.status === 'draft' && canRetireRule(item.state) && !item.retiredEffectiveYear &&
                (year > item.effectiveYear || (year === item.effectiveYear && monthNumber >= item.effectiveMonth)) &&
                <button type="button" className="salary-payroll__secondary-action" disabled={busy || !retirementReason.trim()}
                  onClick={() => void reviewRetirement('rate', item)}>Припинити з {String(monthNumber).padStart(2, '0')}.{year}</button>}
            </div>)}
            {retirementReview && <section aria-label="Наслідки припинення правила">
              <h3>Перевірте припинення з {String(monthNumber).padStart(2, '0')}.{year}</h3>
              <p>Причина: {retirementReview.reason}</p>
              <p>Зачіпає {retirementReview.employees.length} працевлаштувань. Історію та погоджені знімки не буде змінено.
                Нижче наведено джерела, а не прогноз сум. Після припинення оновіть розрахунок і перевірте готовність.
                Старі ревізії цього рівня не відновлюються.</p>
              {retirementReview.employees.length > 0
                ? <ul>{retirementReview.employees.map(item => <li key={item.employmentId}>
                  <strong>{item.fullName}</strong>: {policyScopeLabel(item.beforeSource, options)}
                  {' → '}{policyScopeLabel(item.afterSource, options)}
                </li>)}</ul>
                : <p>Це правило не є джерелом або блокером для працевлаштувань обраного місяця.</p>}
              <button type="button" disabled={busy} onClick={confirmRetirement}>Підтвердити припинення правила</button>
              <button type="button" disabled={busy} className="salary-payroll__secondary-action"
                onClick={() => setRetirementReview(null)}>Не припиняти</button>
            </section>}
          </div>
          </section>
        </details>

        <details className="salary-payroll__disclosure" open={disclosures.bonuses} onToggle={onDisclosureToggle('bonuses')}>
          <summary>Бонуси та штрафи за місяць</summary>
          <section className="salary-payroll__policy-settings">
          {canEdit && period.exists && period.status === 'draft' && <>
            <form onSubmit={saveDraftInput}>
              <label>Працевлаштування<select required value={selectedEmploymentId} onChange={event => setSelectedEmploymentId(event.target.value)}>
                {period.employees.map(item => <option key={item.employmentId} value={item.employmentId}>{item.employeeName}</option>)}
              </select></label>
              {(['fixedBonus', 'bonusPercent', 'fixedPenalty', 'penaltyPercent'] as const).map(field => <label key={field}>
                {field === 'fixedBonus' ? 'Фіксований бонус, ₴' : field === 'bonusPercent' ? 'Бонус, %' : field === 'fixedPenalty' ? 'Фіксований штраф, ₴' : 'Штраф, %'}
                <input type="number" min="0" step={field.endsWith('Percent') ? '0.0001' : '0.01'}
                  value={draftInputs[Number(selectedEmploymentId)]?.[field] ?? '0'}
                  onChange={event => setDraftInputs(previous => ({
                    ...previous,
                    [Number(selectedEmploymentId)]: {
                      ...(previous[Number(selectedEmploymentId)] ?? { fixedBonus: '0', bonusPercent: '0', fixedPenalty: '0', penaltyPercent: '0' }),
                      [field]: event.target.value,
                    },
                  }))} />
              </label>)}
              <button type="submit" disabled={busy || !selectedEmploymentId}>Зберегти для місяця</button>
            </form>
          </>}
          </section>
        </details>

        {(period.status === 'closed' || period.legacyHallsHistory || period.employees.some(item => item.adjustments.length > 0)) && <details className="salary-payroll__disclosure"
          open={disclosures.adjustments} onToggle={onDisclosureToggle('adjustments')}>
          <summary>Коригування та архівні записи</summary>
          <section className="salary-payroll__policy-settings">
            {canEdit && period.status === 'closed' && <form className="salary-payroll__adjustment" onSubmit={addAdjustment}>
              <h2>Коригування після закриття</h2>
              <label>Працевлаштування<select required value={adjustmentEmploymentId} onChange={event => { setAdjustmentEmploymentId(event.target.value); setAdjustmentKey(''); }}>
                {period.employees.map(item => <option key={item.employmentId} value={item.employmentId}>{item.employeeName}</option>)}
              </select></label>
              <label>Сума (+ / −)<input required type="number" step="0.01" value={adjustmentAmount} onChange={event => { setAdjustmentAmount(event.target.value); setAdjustmentKey(''); }} /></label>
              <label>Причина<input required maxLength={1000} value={adjustmentReason} onChange={event => { setAdjustmentReason(event.target.value); setAdjustmentKey(''); }} /></label>
              <button type="submit" disabled={busy || !adjustmentEmploymentId}>Додати коригування</button>
            </form>}
            {period.legacyHallsHistory && <section className="salary-payroll__adjustments">
              <h2>Архівна зарплата HALLS</h2>
              <p>Незмінний історичний знімок; нові ресторанні розрахунки його не читають як джерело.</p>
              <p>{period.legacyHallsHistory.departmentNameSnapshot} · виторг замовлень {currency.format(period.legacyHallsHistory.orderRevenueSnapshot)} · фонд {currency.format(period.legacyHallsHistory.totalPoolSnapshot)}</p>
              {period.legacyHallsHistory.employees.map((employee, index) => <p key={`${employee.employeeName}-${index}`}>{employee.employeeName}: {currency.format(employee.amount)}</p>)}
            </section>}
            {period.employees.some(item => item.adjustments.length > 0) && <section className="salary-payroll__adjustments">
              <h2>Окремі коригування</h2>
              {period.employees.flatMap(item => item.adjustments.map((adjustment, index) =>
                <p key={`${item.employmentId}-${index}`}>{item.employeeName}: {currency.format(adjustment.amount)} — {adjustment.reason} ({adjustment.createdByUsername})</p>))}
            </section>}
          </section>
        </details>}
      </>}
    </main>
  );
};

export default RestaurantPayrollPage;
